import httpMethod from 'services/update2025/httpMethod';
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";

import { Confirmation } from "components/Confirmation";
import { SecQuestionContext } from "components/sec-question/SecQuestionContext";
import { SecQuestionEditor } from "components/sec-question/SecQuestionEditor";

import BlockDetail from "components/block-detail/BlockDetail";
import { Can } from "configs/CASLContext";
import { Dropdown } from "primereact/dropdown";
import { deleteQuestion, deleteQuestions, findQuestions } from "services/SecQuestionService";
import { actionUpdate, actionView } from "utils/action";
import { edit, SecQuestion } from "utils/constants";
import { handleError } from "utils/handleError";

export const SecQuestionList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["sec-question", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const questionContext = useContext(SecQuestionContext);
    const { question, resetQuestion, setQuestion, questions, setQuestions, submitted, setSubmitted, setQuestionDialog } = questionContext;

    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [deleteQuestionDialog, setDeleteQuestionDialog] = useState(false);
    const [deleteQuestionsDialog, setDeleteQuestionsDialog] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchData, setSearchData] = useState({
        content: null,
    });

    useEffect(() => {
        searchQuestions();
    }, []);

    useEffect(() => {
        if (submitted) {
            searchQuestions();
            setSubmitted(false);
        }
    }, [submitted]);

    const searchQuestions = () => {
        setSelectedQuestion(null);
        setQuestion(null);
        findQuestions(headers)
            .then((res) => {
                let dataRes = res.data;
                if (searchData.content?.trim().toLowerCase()) {
                    dataRes = dataRes
                        ?.map((e) => {
                            return {
                                ...e,
                                content: e.content.toLowerCase(),
                            };
                        })
                        ?.filter((x) => x.content.includes(searchData.content?.trim()?.toLowerCase()));
                }
                dataRes?.forEach((e, i) => {
                    e.stt = 1 + i;
                });
                setTotalRecords(dataRes.length);
                setQuestions(dataRes);
            })
            .catch((error) => handleError(toast, t, error));
    };

    const openNewDialog = () => {
        resetQuestion();
        setSubmitted(false);
        setQuestionDialog(true);
    };

    const openEditDialog = () => {
        setQuestion({ ...selectedQuestion, action: actionUpdate });
        setSubmitted(false);
        setQuestionDialog(true);
    };

    const openViewDialog = (question) => {
        setQuestion({ ...question, action: actionView });
        setSubmitted(false);
        setQuestionDialog(true);
    };

    const hideDeleteQuestionsDialog = () => {
        setDeleteQuestionsDialog(false);
    };

    const confirmDeleteQuestion = (question) => {
        setSelectedQuestion(question);
        setDeleteQuestionDialog(true);
    };

    const hideDeleteQuestionDialog = () => {
        setDeleteQuestionDialog(false);
    };

    const confirmDeleteSelected = () => {
        setDeleteQuestionsDialog(true);
    };

    const deleteQuestionAction = () => {
        deleteQuestion(headers, question.id)
            .then(() => {
                searchQuestions();
                setDeleteQuestionDialog(false);
                resetQuestion();
                toast.current.show({
                    severity: "success",
                    summary: t("success", { ns: "common" }),
                    detail: t("inform.deleted", { ns: "common", object: t("question") }),
                    life: 3000,
                });
            })
            .catch((error) => handleError(toast, t, error));
    };

    const deleteSelectedQuestionsAction = () => {
        let ids = [selectedQuestion.id];
        deleteQuestions(headers, ids)
            .then(() => {
                searchQuestions();
                setDeleteQuestionsDialog(false);
                setSelectedQuestion(null);
                toast.current.show({
                    severity: "success",
                    summary: t("success", { ns: "common" }),
                    detail: t("inform.deleted", { ns: "common", object: t("questions") }),
                    life: 3000,
                });
            })
            .catch((error) => handleError(toast, t, error));
    };

    const actionTemplate = () => {
        return (
            <div className="grid">
                <div className="col-12 tex-center mt-2" style={{ display: "flex", justifyContent: "center" }}>
                    {/* tạo mới */}
                    <Button label={t("new", { ns: "common" })} icon="pi pi-plus" className="p-button mr-2" onClick={openNewDialog} />
                    {/* sửa */}
                    <Button disabled={!selectedQuestion} label={t("edit", { ns: "common" })} icon="pi pi-pencil" className="p-button mr-2" onClick={openEditDialog} />
                    {/* xóa */}
                    <Button disabled={!selectedQuestion} label={t("delete", { ns: "common" })} icon="pi pi-trash" className="p-button-danger p-button mr-2" onClick={confirmDeleteSelected} />
                </div>
            </div>
        );
    };

    const checkboxBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <Checkbox checked={rowData.enabled} readOnly />
                </div>
            </>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                <Can I={edit} a={SecQuestion}>
                    <Button icon="pi pi-eye" className="p-button-rounded p-button mr-2" tooltip={t("view", { ns: "common" })} onClick={() => openViewDialog(rowData)} />
                </Can>
            </div>
        );
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    const searchOption = (
        <div className="grid">
            {/* Nội dung */}
            <div className="col-12 md:col-4">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="content"
                    >
                        {t("question.content")}
                    </label>
                    <div className="col">
                        <InputText
                            style={{
                                width: "100%",
                            }}
                            id="content"
                            name="content"
                            onChange={(e) => {
                                setSearchData({
                                    ...searchData,
                                    content: e.target.value,
                                });
                            }}
                        ></InputText>
                    </div>
                </div>
            </div>

            <div className="col-12 md:col-8 text-right">
                <div className="grid custom-form">
                    <div className="col-12">
                        <label
                            style={{
                                // width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                        >
                            {" "}
                        </label>
                    </div>
                    <div className="col-12" style={{ margin: "5px 0px 0px 0px" }}>
                        <Button
                            label={t("search", { ns: "common" })}
                            icon="pi pi-search"
                            onClick={(e) => {
                                searchQuestions();
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("question.title")?.toUpperCase()}</h4>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("search", { ns: "common" })}>{searchOption}</BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("question.list")}>
                                <Toast ref={toast} />
                                <DataTable
                                    ref={dt}
                                    value={questions}
                                    selectionMode="single"
                                    selection={selectedQuestion}
                                    onSelectionChange={(e) => setSelectedQuestion(e.value)}
                                    dataKey="id"
                                    className="datatable-responsive p-datatable-sm"
                                    paginator
                                    rows={10}
                                    showGridlines
                                    responsiveLayout="scroll"
                                    paginatorClassName="justify-content-start"
                                    paginatorTemplate={paginatorTemplate}
                                    emptyMessage="Không có dữ liệu"
                                    scrollable
                                >
                                    <Column field="stt" header={t("stt", { ns: "common" })} style={{ maxWidth: "50px" }} />
                                    <Column field="content" header={t("content")} sortable style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="enabled" header={t("enabled")} dataType="boolean" sortable body={checkboxBodyTemplate} headerStyle={{ width: "10rem" }} style={{ maxWidth: "130px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="modifiedDate" header={t("modifiedDate", { ns: "common" })} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="modifiedBy.username" header={t("modifiedBy", { ns: "common" })} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column body={actionBodyTemplate} style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                </DataTable>
                            </BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">{actionTemplate()}</div>
                    </div>

                    <Confirmation
                        visible={deleteQuestionDialog}
                        hideAction={hideDeleteQuestionDialog}
                        processAction={deleteQuestionAction}
                        confirmMessage={
                            question && (
                                <span>
                                    {t("confirm.delete", { ns: "common", object: t("question") })} <b>{question.name}</b>?
                                </span>
                            )
                        }
                    />

                    <Confirmation
                        visible={deleteQuestionsDialog}
                        hideAction={hideDeleteQuestionsDialog}
                        processAction={deleteSelectedQuestionsAction}
                        confirmMessage={
                            selectedQuestion && (
                                <span>
                                    {t("confirm.delete.selected", { ns: "common" })}
                                    <b>{selectedQuestion.content}</b>
                                </span>
                            )
                        }
                    />
                    <SecQuestionEditor />
                </div>
            </div>
        </div>
    );
};
